import { Fragment, useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { Banner, Checkbox, Input, Typography } from '@weareopenr/component-library';
import classnames from 'classnames/bind';

import { pluralize } from 'src/helpers/pluralize/pluralize';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes/usePriceSchemes';
import { useSelectedPriceScheme } from 'src/hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { IOptionGroupValues } from '../CreateOptionGroupModal/types';

import styles from './OptionGroupPricing.module.scss';

const cx = classnames.bind(styles);

interface IOptionGroupPricingProps {
  formReturn: UseFormReturn<IOptionGroupValues>;
}

export const OptionGroupPricing = ({ formReturn }: IOptionGroupPricingProps) => {
  const {
    register,
    setValue,
    control,
    formState: { errors },
    trigger,
  } = formReturn;
  const { selectedPriceSchemeId } = useSelectedPriceScheme();
  const { data: allPriceSchemes = [] } = usePriceSchemes();
  const optionGroupName = useWatch({ control, name: 'name' });
  const modifiers = useWatch({ control, name: 'modifiers' });
  const priceSchemeIds = useWatch({ control, name: 'price_scheme_ids' });

  const resolvedPriceSchemeIds = useMemo(() => {
    if (priceSchemeIds?.length) return priceSchemeIds;
    if (selectedPriceSchemeId) return [selectedPriceSchemeId];
    return [];
  }, [priceSchemeIds, selectedPriceSchemeId]);

  const priceSchemes = useMemo(
    () => allPriceSchemes.filter((scheme) => resolvedPriceSchemeIds.includes(scheme.id)),
    [allPriceSchemes, resolvedPriceSchemeIds],
  );

  const priceSchemesNames = useMemo(() => priceSchemes.map((scheme) => scheme.name).join(', '), [priceSchemes]);

  return (
    <div className={cx('flex-column', 'container')}>
      <div>
        <Typography size="xxlarge" className={styles['heading-text']}>
          Set prices for <span className={cx('bold-text')}>{optionGroupName}</span>
        </Typography>
        <Typography>Define the prices of products in your group for a specific Price Scheme</Typography>
      </div>
      <Typography>
        {pluralize('Price Scheme', priceSchemes.length)} - <span className={cx('bold-text')}>{priceSchemesNames}</span>
      </Typography>
      <div className={cx('grid')}>
        <Typography weight="bold">Product Name</Typography>
        <Typography weight="bold">Inherit Product Price</Typography>
        <Typography weight="bold">Override Price*</Typography>
        {modifiers?.map((field, index) => (
          <Fragment key={field.optionItemId}>
            <Typography className={cx('truncate-text')}>{modifiers[index].name}</Typography>
            <div className={cx('checkbox')}>
              <Checkbox
                checked={modifiers[index].useDefaultPrice}
                onChange={() => {
                  setValue(`modifiers.${index}.useDefaultPrice`, !modifiers[index].useDefaultPrice);
                  setValue(`modifiers.${index}.valueWithTax`, null, { shouldValidate: true });
                }}
              />
            </div>
            <Input
              type="number"
              step={0.01}
              label="Price (£)"
              disabled={field.useDefaultPrice}
              register={register(`modifiers.${index}.valueWithTax`, {
                onChange: () => {
                  trigger('modifiers');
                },
                valueAsNumber: true,
                validate: (value, formValues) => {
                  if (!formValues.modifiers[index].useDefaultPrice && !value && value !== 0)
                    return 'Please enter a price';
                },
              })}
              error={errors?.modifiers?.[index]?.valueWithTax?.message}
            />
          </Fragment>
        ))}
      </div>
      <Banner
        text={
          <>
            <strong>Quick note</strong> - These prices will only be added to your {priceSchemesNames}{' '}
            {pluralize('scheme', priceSchemes.length)}. In order to use this group with alternative schemes, you will
            need to enter prices within those schemes
          </>
        }
      />
    </div>
  );
};

export default OptionGroupPricing;
